// Standard library imports
import React, { useState, useEffect } from 'react';
// External library imports
import { toast } from 'react-toastify';
import moment from 'moment/moment';
import { UilInfoCircle, UilImport } from '@iconscout/react-unicons';
// Internal module imports
import { useLoader, useQueryParamWithDefault } from '../../../../hooks';
import Typography from '../../../../components/Typography/Typography';
import Table from '../../../../components/Table/Table';
import { momentTimeFormater } from '../../../../utils/dateHelper';
import DatePicker from '../../../../components/Inputs/DatePicker/DatePicker';
import classes from '../../../../styles/AllDevices.module.css';
import { RootAfrrService } from '../../../../services/RootAfrrService';
import CustomTooltip from '../../../../components/CustomToolTip/CustomTooltip';
import ModalComponent from '../../../../components/ModalComponent/ModalComponent';
import afrrClasses from './index.module.css';
import Info from './ModalComponent/Info';
import { DropdownComponent } from '../../../../components/Inputs/Input';
import { DateParam, StringParam } from 'use-query-params';
import IconComponent from '../../../../components/Icon/Icon';
import { exportData } from '../../../../utils/downloadHelper';

const LIMIT = 15;

const AfrrLogs = ({ eicCodeOptions, cId }) => {
    const curCetDateTime = moment().tz('Europe/Berlin');
    const [startLoader, stopLoader] = useLoader();
    const [date, setDate] = useQueryParamWithDefault('date', DateParam, new Date(curCetDateTime.format()), cId);
    const [tableData, setTableData] = useState([]);
    const [skip, setSkip] = useState(0);
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [infoModal, setInfoModal] = useState({ status: false, data: {} });
    const [selectedEicCode, setSelectedEicCode] = useQueryParamWithDefault('eic', StringParam, eicCodeOptions[0]?.value, cId);

    useEffect(() => {
        if (eicCodeOptions?.length) setSelectedEicCode(selectedEicCode ? selectedEicCode : eicCodeOptions[0]?.value);
    }, [eicCodeOptions]);

    useEffect(() => {
        if (selectedEicCode) getLogs();
    }, [selectedEicCode, date, skip]);

    const getLogs = (paginatedData = true) => {
        const params = {
            limit: paginatedData ? LIMIT : 50000,
            skip: paginatedData ? skip : 0,
            date: moment(date).format('YYYY-MM-DD'),
            eicCode: selectedEicCode,
        };
        const handler = paginatedData ? handleSuccess : handleDownload;
        RootAfrrService.GetLogs(params, startLoader, handler, handleError, stopLoader);
    };

    const handleSuccess = ({ data }) => {
        const processedData = data?.data[0]?.data?.map((item) => ({
            source: item.source,
            type: item.type,
            setPoint: item?.value?.setPoint || item?.value?.setPoint === 0 ? item?.value?.setPoint : '--',
            status: item?.value?.status || '--',
            activationDirection: item?.value?.activationDirection || '--',
            action: item?.action || '--',
            mode: item?.value?.mode || '--',
            response: item?.value?.response ? (
                <div className={afrrClasses.ResponseContainer}>
                    {item?.value?.response}
                    <CustomTooltip content={'Info'}>
                        <UilInfoCircle
                            size={'1.2vw'}
                            style={{ color: 'var(--color-primary)' }}
                            onClick={() =>
                                setInfoModal({
                                    status: true,
                                    data: item?.value || {},
                                })
                            }
                        />
                    </CustomTooltip>
                </div>
            ) : (
                '--'
            ),
            timestamp: item?.timestamp ? momentTimeFormater(item.timestamp).format('YYYY-MM-DD HH:mm:ss') : '--',
        }));
        setTableData(processedData);
        setTotalCount(data?.data[0]?.count.length ? data?.data[0]?.count[0]?.count : 0);
    };

    const handleDownload = ({ data }) => {
        exportData(data?.data[0]?.data, 'aFRR Logs', [
            'timestamp',
            'source',
            'type',
            'value_setPoint',
            'value_status',
            'value_activationDirection',
            'action',
            'value_mode',
            'value_response',
        ]);
    };

    const handleError = (err) => {
        console.log(err);
        if (err && err.response) toast.error(err.response.data.message);
    };

    const handlePageChange = (pageno) => {
        setSkip(LIMIT * pageno);
        setPage(pageno);
    };

    return (
        <div>
            <ModalComponent isOpen={infoModal.status} setOpen={() => setInfoModal({ status: false, data: {} })}>
                <Info data={infoModal.data} setInfoModal={setInfoModal} />
            </ModalComponent>
            <div className={afrrClasses.Filter}>
                <div className={afrrClasses.OuterContainer}>
                    <div className={afrrClasses.FilterContainer}>
                        <div>
                            <div className={classes.FieldControl}>
                                <label>Select EIC Code</label>
                                <DropdownComponent
                                    name="eicCode"
                                    options={eicCodeOptions}
                                    defaultValue={{ value: selectedEicCode }}
                                    onChange={(e) => {
                                        if (e.value !== selectedEicCode) {
                                            setSelectedEicCode(e.value);
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={afrrClasses.FilterContainer} style={{ display: 'flex', alignItems: 'end' }}>
                        <div className={classes.FieldControl2}>
                            <label>Date</label>
                            <DatePicker date={date} setDate={setDate} showArrow={true} />
                        </div>
                        <div onClick={() => getLogs(false)}>
                            <IconComponent icon={UilImport} tooltip={'Download'} size="1.5vw" />
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.AllDevices}>
                <div className={classes.Header}>
                    <div>
                        <Typography content="Logs" />
                        <div className={classes.TableCount}>
                            Total Count :
                            <span>
                                <Typography size="14" content={totalCount || 0} />
                            </span>
                        </div>
                    </div>
                </div>
                <div>
                    <div>
                        <Table
                            head={['Source', 'Type', 'Timestamp (CET)', 'Set Point', 'Status', 'Activation Direction', 'Action', 'Mode', 'Response']}
                            keys={['source', 'type', 'timestamp', 'setPoint', 'status', 'activationDirection', 'action', 'mode', 'response']}
                            data={tableData}
                            page={page}
                            Pagination={true}
                            limit={LIMIT}
                            handlePageChange={handlePageChange}
                            totalCount={totalCount}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AfrrLogs;
